import { Store, select } from '@ngrx/store';
import { ParamMap } from '@angular/router';
import { Injectable } from '@angular/core';
import { map, take, filter, switchMap } from 'rxjs/operators';
import { Observable, zip } from 'rxjs';

import { State } from '@app/store/state/state';
import { BusSearchPayload } from '@buses/bus-results/entities/bus-search-payload';
import {
  searchBusesAction,
  searchBusesForMobileAction,
  searchBusesForDesktopAction,
  clearBusesResultsAction,
  fetchBusesOptionAction,
  fetchBusesDataSearchDeeplinkAction
} from '@buses/bus-results/store/actions/bus-results.actions';
import { BusResultDesktop, BusResultsMobile, SearchResultsData, BusItem } from '@app/entities/bus-results';
import { busResultsRootSelector } from '@buses/bus-results/store/selectors/bus-results-root.selector';
import { getUiBusesState } from '@buses/bus-results/store/selectors/ui.selector';
import { UiBusesState } from '@buses/bus-results/store/state/bus-results-module.state';
import { SearchPayload } from '@buses/bus-results/entities/request-search.payload';
import { FilterApplier } from '@shared/bus-itinerary/entities/bus-filters';
import { SorterTypes } from '@shared/filters/entities/sorter-options';
import { BusClassTypes } from '@buses/bus-results/entities/bus-type';
import { BusType } from '@search-bar/entities/bus-type';
import { PassengerArrangement } from '@shared/base-search-bar/entities/passenger-arrangement';
import { selectPassengerArrangement } from '@shared/base-search-bar/store/selectors/passenger-arrangement.selector';
import { flightKeysMapper } from '@app/modules/flight-results/mappers/flight-keys.mapper';
import { ErrorHandle } from '@shared/no-results/entities/results-errors';
import { GlobalFacade } from '@app/global.facade';
import { AirFilterResponseVm, AirFilterVm } from '@shared/magic-filter-bar/entities/filter-types';
import { AppFacade } from '@app/app.facade';
import { mapAirFilterPayload } from '@app/mappers/global-filters/air-global-filters.mapper';

@Injectable()
export class BusResultsFacade {

  private countResultsMobile: number = 40;
  private countResultsDesktop: number = 20;
  constructor(
    private readonly store: Store<State>,
    private readonly globalFacade: GlobalFacade,
    private readonly appFacade: AppFacade,
  ) { }

  public filters$: Observable<AirFilterResponseVm> = this.globalFacade.airFilters$;
  public busesFilters$: Observable<AirFilterVm[]> = this.appFacade.busCompaniesEntities$.pipe(
    switchMap(busCompaniesEntities => this.globalFacade.airFilterVm$(busCompaniesEntities, true))
  );

  public passengerArrangementsState$: Observable<PassengerArrangement[]> = this.store
    .pipe(
      select(selectPassengerArrangement)
    );

  public busesResultsMobile$: Observable<BusResultsMobile[]> = this.store
    .pipe(
      select(busResultsRootSelector),
      map(state => state.busesResultsMobile)
    );

  public busesResultsDesktop$: Observable<BusResultDesktop[]> = this.store
    .pipe(
      select(busResultsRootSelector),
      map(state => state.busesResultsDesktop)
    );

  public selectedItinerary$: Observable<string> = this.store
    .pipe(
      select(getUiBusesState),
      map((state: UiBusesState) => state.itinerarySelected)
    );

  public isLoadingBuses$: Observable<boolean> = this.store
    .pipe(
      select(getUiBusesState),
      map((state: UiBusesState) => state.isLoadingBuses)
    );

  public isLoadingFilters$: Observable<boolean> = this.store
    .pipe(
      select(getUiBusesState),
      map((state: UiBusesState) => state.isLoadingFilters)
    );

  public errorInBuses$: Observable<boolean> = this.store
    .pipe(
      select(getUiBusesState),
      map((state: UiBusesState) => state.errorInBuses.status)
    );

  public searchResultsData$: Observable<SearchResultsData> = this.store
    .pipe(
      select(busResultsRootSelector),
      map(state => state.searchResultsData)
    );

  public errors$: Observable<ErrorHandle> = this.globalFacade.ui.errorHandle$;

  public searchPayload$: Observable<SearchPayload> = this.store
    .pipe(
      select(busResultsRootSelector),
      map(state => state.searchPayload)
    );
  public orderCriteria$: Observable<SorterTypes> = this.searchPayload$.pipe(
    map(payload => payload.OrderCriteria)
  );


  public searchBuses(payload: BusSearchPayload): void {
    this.store.dispatch(searchBusesAction({ payload: payload }));
  }

  public searchBusesWithFilter(filterApplier: FilterApplier, isMobile: boolean): void {
    this.store.dispatch(clearBusesResultsAction({ isMobile }));
    this.searchPayload$.pipe(take(1)).subscribe(searchPayload => {
      if (isMobile) {
        this.store.dispatch(searchBusesForMobileAction({ 
          payload: mapAirFilterPayload(filterApplier, searchPayload) as SearchPayload
        }));
      } else {
        this.store.dispatch(searchBusesForDesktopAction({ 
          payload: mapAirFilterPayload(filterApplier, searchPayload)as SearchPayload
        }));
      }
    }).unsubscribe();
  }

  public selectBuses(selectedBuses: BusItem[]): void {
    const busKeys = flightKeysMapper(selectedBuses);
    this.searchPayload$.pipe(take(1)).subscribe(searchPayload => {
      this.store.dispatch(fetchBusesOptionAction({
        payload: {
          SearchId: searchPayload.SearchId,
          SessionToken: searchPayload.SessionToken,
          FlightKeys: busKeys
        }
      }));
    }).unsubscribe();
  }

  public searchBusesWithPagination(isMobile: boolean): void {
    zip(
      this.searchPayload$,
      this.searchResultsData$
    ).pipe(
      take(1),
      map(data => {
        return {
          searchPayload: data[0],
          searchResultsData: data[1]
        };
      }),
      filter(data => data.searchResultsData.ResultsCount > data.searchPayload.ResultCountUpperBound)
    ).subscribe(data => {
      const payload = {
        payload: {
          ...data.searchPayload,
          ResultCountLowerBound: data.searchPayload.ResultCountUpperBound + 1,
          ResultCountUpperBound: data.searchPayload.ResultCountUpperBound +
            (isMobile ? this.countResultsMobile : this.countResultsDesktop),
        }
      };
      isMobile ?
        this.store.dispatch(searchBusesForMobileAction(payload)) :
        this.store.dispatch(searchBusesForDesktopAction(payload));
    }).unsubscribe();
  }

  public fetchBusesDataSearchDeeplinkAction
  (paramsMap: ParamMap, busType: BusType, busClass: BusClassTypes): void {
    this.store.dispatch(fetchBusesDataSearchDeeplinkAction({ paramsMap, busType, busClass }));
  }
}
